import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Loader2, Trash2, Plus, Car, Bike, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { API } from '../../../lib/api';
import { formatErr, notifyErr, inputCls } from '../../../components/admin/billing/shared';

const TYPE_LABEL = { car: 'Car', motorcycle: 'Motorcycle', scooter: 'Scooter' };
const TYPE_ICON = { car: Car, motorcycle: Bike, scooter: Bike };

const VehicleCatalog = () => {
  const { authHeader } = useAuth();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [draft, setDraft] = useState({ type: 'car', make: '', model: '' });
  const [adding, setAdding] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setErr('');
    try {
      const { data } = await axios.get(`${API}/billing/vehicles/catalog/custom`, { headers: authHeader() });
      setEntries(data);
    } catch (e) {
      setErr(formatErr(e));
    } finally {
      setLoading(false);
    }
  }, [authHeader]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleAdd = async (e) => {
    e?.preventDefault();
    if (!draft.make.trim() || !draft.model.trim()) return;
    setAdding(true);
    try {
      await axios.post(
        `${API}/billing/vehicles/catalog/entry`,
        { type: draft.type, make: draft.make.trim(), model: draft.model.trim() },
        { headers: authHeader() },
      );
      setDraft({ type: draft.type, make: '', model: '' });
      fetchAll();
    } catch (e) {
      notifyErr(e);
    } finally {
      setAdding(false);
    }
  };

  const handleDelete = async (entry) => {
    if (!window.confirm(`Remove "${entry.make} ${entry.model}" from the catalog?`)) return;
    try {
      await axios.delete(
        `${API}/billing/vehicles/catalog/entry`,
        {
          headers: authHeader(),
          data: { type: entry.type, make: entry.make, model: entry.model },
        },
      );
      fetchAll();
    } catch (e) {
      notifyErr(e);
    }
  };

  return (
    <div className="min-h-screen px-4 sm:px-6 py-6">
      <div className="max-w-3xl mx-auto">
        <Link
          to="/admin/settings"
          className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700 mb-3"
        >
          <ArrowLeft size={14} /> Back to Settings
        </Link>
        <div className="mb-5">
          <h1 className="text-xl font-semibold tracking-tight text-slate-900">Vehicle Catalog</h1>
          <p className="text-sm text-slate-500">
            Custom makes &amp; models added on top of the built-in Indian catalog. These
            show up as suggestions in the customer form. Built-in entries can&apos;t be
            removed here — they come from the seed file.
          </p>
        </div>

        {/* Add form */}
        <form
          onSubmit={handleAdd}
          className="bg-white border border-slate-200 rounded-xl p-4 mb-6"
          data-testid="catalog-add-form"
        >
          <div className="grid grid-cols-12 gap-2 items-end">
            <div className="col-span-12 sm:col-span-3">
              <label className="block text-xs uppercase tracking-wider text-slate-500 mb-1">Type</label>
              <select
                value={draft.type}
                onChange={(e) => setDraft({ ...draft, type: e.target.value })}
                className={inputCls}
                data-testid="catalog-add-type"
              >
                <option value="car">Car</option>
                <option value="motorcycle">Motorcycle</option>
                <option value="scooter">Scooter</option>
              </select>
            </div>
            <div className="col-span-6 sm:col-span-4">
              <label className="block text-xs uppercase tracking-wider text-slate-500 mb-1">Make</label>
              <input
                value={draft.make}
                onChange={(e) => setDraft({ ...draft, make: e.target.value })}
                placeholder="e.g. VinFast"
                className={inputCls}
                data-testid="catalog-add-make"
              />
            </div>
            <div className="col-span-6 sm:col-span-3">
              <label className="block text-xs uppercase tracking-wider text-slate-500 mb-1">Model</label>
              <input
                value={draft.model}
                onChange={(e) => setDraft({ ...draft, model: e.target.value })}
                placeholder="e.g. VF 9"
                className={inputCls}
                data-testid="catalog-add-model"
              />
            </div>
            <div className="col-span-12 sm:col-span-2">
              <button
                type="submit"
                disabled={adding || !draft.make.trim() || !draft.model.trim()}
                className="w-full inline-flex items-center justify-center gap-1 bg-teal-500 hover:bg-teal-600 text-white text-sm font-medium px-3 py-2 rounded-lg disabled:opacity-50"
                data-testid="catalog-add-submit"
              >
                {adding ? <Loader2 className="animate-spin" size={14} /> : <Plus size={14} />} Add
              </button>
            </div>
          </div>
        </form>

        {err && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2 mb-4">{err}</div>}

        {/* List */}
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          {loading ? (
            <div className="text-center text-slate-500 py-12"><Loader2 className="animate-spin inline mr-2" size={18} /> Loading…</div>
          ) : entries.length === 0 ? (
            <div className="text-center text-slate-500 py-12">
              No custom entries yet. Add one above, or save make/model from the customer form
              with the bookmark button.
            </div>
          ) : (
            <table className="w-full text-sm" data-testid="catalog-table">
              <thead className="bg-slate-50 text-slate-500 text-[11px] uppercase tracking-wider">
                <tr>
                  <th className="text-left px-4 py-3 font-semibold w-32">Type</th>
                  <th className="text-left px-4 py-3 font-semibold">Make</th>
                  <th className="text-left px-4 py-3 font-semibold">Model</th>
                  <th className="text-right px-4 py-3 font-semibold w-24">Remove</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((e) => {
                  const Icon = TYPE_ICON[e.type] || Car;
                  return (
                    <tr key={e.id} className="border-t border-slate-200" data-testid={`catalog-row-${e.id}`}>
                      <td className="px-4 py-3 text-slate-700">
                        <span className="inline-flex items-center gap-1.5">
                          <Icon size={13} /> {TYPE_LABEL[e.type] || e.type}
                        </span>
                      </td>
                      <td className="px-4 py-3 font-medium text-slate-900">{e.make}</td>
                      <td className="px-4 py-3 text-slate-700">{e.model}</td>
                      <td className="px-4 py-3 text-right">
                        <button
                          onClick={() => handleDelete(e)}
                          className="p-1.5 rounded hover:bg-red-100 text-red-600"
                          title="Remove"
                          data-testid={`catalog-delete-${e.id}`}
                        >
                          <Trash2 size={15} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default VehicleCatalog;
